import type { EventHandler, OnlineStatusHandler, RecoveryHandler } from "@/plugins/ws.types";
import type { WSConnection } from "@/plugins/ws.connection";

export interface WSModel {
  recoveryHandler?: RecoveryHandler;
  onlineStatusHandler?: OnlineStatusHandler;

  handleWSData(data: any): void | undefined;
}

export interface WSServiceSettings {
  command?: string;
  eventHandler?: EventHandler;
  recoveryHandler?: RecoveryHandler;
  onlineStatusHandler?: OnlineStatusHandler;
  ignoreCommands?: string[];
}

const DEFAULT_IGNORED_COMMANDS = ["ping", "pong", "token"];

export abstract class AbstractWSService {
  protected connection: WSConnection;
  protected settings: WSServiceSettings;
  private isSubscribed = false;
  private subscriptionData: Record<string, any> | null = null;
  private originalEventHandler: EventHandler | undefined = undefined;
  private originalRecoveryHandler: RecoveryHandler | undefined = undefined;

  protected constructor(connection: WSConnection, settings: WSServiceSettings) {
    this.connection = connection;
    this.settings = settings;
  }

  subscribe(): void {
    if (this.isSubscribed) {
      throw new Error("Already subscribed to this WebSocket service");
    }

    this.isSubscribed = true;

    if (this.settings.command) {
      // Send subscription command
      this.connection.sendCommand(this.settings.command, this.subscriptionData);
    }

    // Set up an event handler
    this.originalEventHandler = this.connection.eventHandler;
    this.connection.eventHandler = (data: any) => {
      const ignored = this.settings.ignoreCommands ?? DEFAULT_IGNORED_COMMANDS;

      if (data?.command && ignored.includes(data.command)) {
        return;
      }

      // Call original event handler if exists
      if (this.originalEventHandler) {
        this.originalEventHandler(data);
      }

      // Call a service-specific event handler if exists
      if (this.settings.eventHandler) {
        this.settings.eventHandler(data);
      }
    };

    if (this.settings.recoveryHandler) {
      const recoveryHandler = this.settings.recoveryHandler;
      this.originalRecoveryHandler = this.connection.recoveryHandler;
      this.connection.recoveryHandler = async (lastEvent) => {
        await this.originalRecoveryHandler?.(lastEvent);
        await recoveryHandler(lastEvent);
      };
    }

    if (this.settings.onlineStatusHandler) {
      this.connection.onlineStatusHandler = this.settings.onlineStatusHandler;
    }
  }

  unsubscribe(): void {
    if (!this.isSubscribed) {
      return;
    }

    this.isSubscribed = false;
    this.connection.eventHandler = this.originalEventHandler;
    if (this.settings.recoveryHandler) {
      this.connection.recoveryHandler = this.originalRecoveryHandler;
    }
    // Send unsubscribe command if needed
    if (this.settings.command) {
      this.connection.sendCommand(`${this.settings.command}_unsubscribe`, this.subscriptionData);
    }
    this.subscriptionData = null;
    this.originalEventHandler = undefined;
    this.originalRecoveryHandler = undefined;
  }

  sendCommand(command: string, payload?: Record<string, any>) {
    this.connection.sendCommand(command, payload);
  }
}
